import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'gatsby-plugin-intl'
import Container from 'components/Container'
import ProjectCard from 'components/ProjectCard'
import Slider from 'components/Slider'
import { projects } from './Projects.mock'
import * as S from './Projects.styles'
import useTranslate from 'hooks/useTranslate'
import placeholderEN from 'assets/images/projects/placeholderEN.jpg'
import placeholderPT from 'assets/images/projects/placeholderPT.jpg'

const imgPlaceholder = {
  pt: placeholderPT,
  en: placeholderEN,
}

const settings = {
  slidesToShow: 1,
  slidesToScroll: 1,
  speed: 500,
  infinite: false,
  draggable: false,
  responsive: [
    {
      breakpoint: 1170,
      settings: {
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 768,
      settings: {
        draggable: true,
        slidesToShow: 1.1,
      },
    },
  ],
}

const normalizeItems = (items = []) =>
  items.reduce((acc, group) => {
    if (Array.isArray(group)) return acc.concat(group)
    if (group) acc.push(group)
    return acc
  }, [])

const ProjectsSliderMobile = ({ items, text }) => {
  const translate = useTranslate()
  const { locale } = useIntl()
  const flattenedItems = normalizeItems(items)

  return (
    <S.ProjectsAtMobile>
      {text?.map((item) => (
        <S.Text key={item.textTitle}>
          <S.TextTitle>{item.textTitle}</S.TextTitle>
          <S.TextDescription>
            {translate(item.textDescription)}
          </S.TextDescription>
        </S.Text>
      ))}
      <Slider settings={settings}>
        {flattenedItems.map(({ url, title, description }) => (
          <div key={title}>
            <ProjectCard
              key={url}
              imgUrl={url === 'placeholder' ? imgPlaceholder[locale] : url}
              title={title}
              description={description}
            />
          </div>
        ))}
      </Slider>
    </S.ProjectsAtMobile>
  )
}

ProjectsSliderMobile.propTypes = {
  text: PropTypes.arrayOf(PropTypes.string),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
    })
  ),
}

const Projects = () => {
  const { formatMessage, locale } = useIntl()
  const [activeTab, setActiveTab] = useState(0)
  const [items, setItems] = useState([])
  const [text, setText] = useState([])
  const translate = useTranslate()

  useEffect(() => {
    setItems(projects[activeTab]?.items ?? [])
  }, [activeTab])

  useEffect(() => {
    setText(projects[activeTab]?.text ?? [])
  }, [activeTab])

  const RenderSlider = useCallback(
    () => (
      <S.ProjectsAtDesktop>
        {text?.map((item) => (
          <S.Text key={item.textTitle}>
            <S.TextTitle>{translate(item.textTitle)}</S.TextTitle>
            <S.TextDescription>
              {translate(item.textDescription)}
            </S.TextDescription>
          </S.Text>
        ))}
        <Slider settings={settings}>
          {items?.map((subItems) => (
            <div key={subItems}>
              <S.ProjectsWrapper>
                {subItems?.map(({ url, title, description }) => (
                  <div key={url}>
                    <ProjectCard
                      key={url}
                      imgUrl={
                        url === 'placeholder' ? imgPlaceholder[locale] : url
                      }
                      title={title}
                      description={description}
                    />
                  </div>
                ))}
              </S.ProjectsWrapper>
            </div>
          ))}
        </Slider>
      </S.ProjectsAtDesktop>
    ),
    [items, text, formatMessage, translate]
  )

  return (
    <S.SectionWrapper>
      <S.WrapperNavbar id="projects">
        <S.ProjectsTitle locale={locale}>
          <S.Title>{formatMessage({ id: 'projects.title' })}</S.Title>
        </S.ProjectsTitle>
        <S.Nav>
          <S.Overflow>
            {projects.map(({ key }, index) => (
              <S.NavItem
                key={key}
                active={index === activeTab}
                onClick={() => setActiveTab(index)}
              >
                {formatMessage({ id: key })}
              </S.NavItem>
            ))}
          </S.Overflow>
        </S.Nav>
      </S.WrapperNavbar>
      <S.Wrapper>
        <Container>
          <RenderSlider />
          <ProjectsSliderMobile items={items} text={text} />
        </Container>
      </S.Wrapper>
    </S.SectionWrapper>
  )
}

export default Projects
